import { native, pageVisibleOnce } from '@forest/native-services'
import { createEffect, createSignal, type Accessor } from 'solid-js'
import type { Paywall } from '~/lib/bootstrap'
import { inCountdownWindow } from '~/lib/expiring-subscription'
import type { Screen } from '~/lib/screen'

type LossScreenPageName =
  `plus_countdown_${'7_days' | '2_days'}_${'free_user' | 'pro_user'}_paywall`

/** The page names the resubscribe paywall logs (GLOSSARY.md). */
type PageName =
  | LossScreenPageName
  | 'plus_countdown_choose_plan_paywall'
  | 'plus_countdown_choose_plan_has_offer_paywall'

/**
 * The screen's page name (GLOSSARY.md "Resubscribe paywall analytics names"):
 * the loss screen by the countdown window at `nowMs` and Pro user, the plan
 * picker by whether it displays the promotional annual plan. `null` when the
 * loss screen has no subscription to show, so renders nothing.
 */
export function pageViewName(
  screen: Screen,
  paywall: Pick<Paywall, 'subscription' | 'plans'>,
  nowMs: number,
): PageName | null {
  if (screen === 'plans') {
    return paywall.plans.promotion
      ? 'plus_countdown_choose_plan_has_offer_paywall'
      : 'plus_countdown_choose_plan_paywall'
  }
  const { subscription } = paywall
  if (!subscription) return null
  const days = inCountdownWindow(subscription.expiresAtMs, nowMs)
    ? '2_days'
    : '7_days'
  const user = subscription.isPremium ? 'pro_user' : 'free_user'
  return `plus_countdown_${days}_${user}_paywall`
}

function logPageView(pageName: PageName) {
  void native.general.logEvent({
    event_name: 'page_view',
    event_params: { page_name: pageName },
  })
}

/**
 * Logs a `page_view` on every entry to a screen of the revealed page, from
 * when native presents it. The name is read on entry: a countdown crossing
 * into its window while the screen stays open does not re-fire.
 */
export function trackPageViews(screen: Accessor<Screen>, paywall: Paywall) {
  const [presented, setPresented] = createSignal(false)
  void pageVisibleOnce.promise.then(() => setPresented(true))
  createEffect(() => {
    if (!presented()) return
    const name = pageViewName(screen(), paywall, Date.now())
    if (name) logPageView(name)
  })
}
